// The gather recipe kit: the arithmetic every Dredger harvesting look draws
// with (R1120, `ui-map-interface.md` §6.2.1).
//
// A GATHER look is what a Dredger draws while it cuts a deposit. One look
// serves the gather and the R715 shaft, and it says the two apart by shape or
// rhythm and never by colour alone. `gather-styles.ts` is the registry that
// answers a World theme look id;
// `gather-recipes-{seabed,matter,hull,field}.ts` hold the draw code of the ten
// picked cards of mockup round 055. This module is what all five import: the
// row and the context types, the eases, the grain, the two alpha clamps, the
// shared inks, the deposit geometry, the atlas cell keys and every primitive.
//
// This is a leaf (`leaves.test.ts`). It names the three sinks of a layer
// through a type-only clause, the way `combat-recipe-kit.ts` does. The layer
// owns the state, the clock, the cull, the atlas and the pools, and it hands
// them in as one reused context. The clamps, the eases and the sink
// primitives every family shares stand in `recipe-kit.ts`; this module binds
// them to `[gather.limits]` and adds what a cut alone draws.
//
// **Nothing here allocates.** A recipe runs once a cut a frame, so every
// scratch is a module constant and every grain is a pure function (R131).
//
// **A recipe is a pure function of the cut's own state and the layer clock.**
// It reads the context and keeps nothing between frames.
//
// ---------------------------------------------------------------------------
// HOW A MOCKUP CARD MAPS ONTO THE KIT
// ---------------------------------------------------------------------------
//
// The cards of `archive/mockups/055-dredger-gather.html` are canvas-2D, and
// every one of them draws in WORLD space with the FIELD at the origin. This
// module is that page's own helper set over the three instanced batches, and
// the translation is fixed:
//
//   | the card writes                | the recipe writes                     |
//   | ------------------------------ | ------------------------------------- |
//   | the canvas origin              | `c.fx` / `c.fy`, added by every call  |
//   | `sc.D[0]`, `sc.D[1]`           | `c.hx`, `c.hy`                        |
//   | `sc.L(x, y)`                   | `inHull(c, x, y, P0)`                 |
//   | `bRestA()`                     | `c.restHead`                          |
//   | `disc(c, …)`                   | `disc(c, …)` → the circle sink        |
//   | `circle(c, …)`                 | `ring(c, …)`, or `dashedRing`         |
//   | `arc(c, …)` (a start and an END)| `arcOf(c, …)` (a start and a SWEEP)   |
//   | `line(c, …)`                   | `line(c, …)` → the segment sink       |
//   | a polyline / a curve           | `polyline(c, …)` → n − 1 segments     |
//   | `glow(c, …)`                   | `glow(c, …)` → the `gather:glow` cell |
//   | `aRingGlow(c, …)`              | `ringGlow(c, …)` → `gather:halo`      |
//   | `bChip(c, …)` filled           | `chip(c, …)` → the `gather:chip` cell |
//   | `bChip(c, …)` stroked          | `chipOutline(c, …)` → four segments   |
//   | a filled triangle / a jaw      | `wedge(c, …)` → the `gather:wedge` cell|
//   | `fillRect` under a rotate      | `rect(c, …)` → the `white` cell       |
//   | `strokeRect`                   | `box(c, …)` → four segments           |
//   | `PF(c, DIAMOND)` + `S`         | `diamond(c, …)` → four segments       |
//   | `hash(k)`                      | `grain(k)`                            |
//   | `aHold` `aTurn` `aSince`       | `holdEnv` `turnSec` `sinceCut`        |
//   | `bStay` `bTau` `cRig`          | `stayEnv` `tauOf` `rigEnv`            |
//   | `dOn` `dBeat` `dQuad`          | `onEnv` `lotBeat` `quadAt`            |
//
// A gradient is the one thing a batch cannot make, so the two radial ramps of
// the cards are baked atlas cells that the quad sink tints and scales
// (`fast/gfx/atlas.ts gatherSpecFor`). The two polygon shapes are baked the
// same way, because the sinks draw no filled polygon.
//
// THE WORKED EXAMPLE, card 07 DRAG LOOP, line for line:
//
//   the card                                     the recipe
//   -------------------------------------------- -------------------------
//   const env = bStay(sc)                        const env = stayEnv(c)
//   c.rotate(bRestA())                           every point through `spin`
//   c.ellipse(0, 0, 3.6, 11)                     ellipse(c, …, row.rPx,
//                                                  row.r2Px, c.restHead, …)
//   c.fillRect(x - 1.4, y - 1.4, 2.8, 2.8)       rect(c, px, py,
//                                                  row.lengthPx, …)
//   c.strokeRect(x - 1.7, …, 3.4, 3.4)           box(c, px, py,
//                                                  row.lengthPx
//                                                  + row.width2Px, …)
//
// Every FIGURE came out of the card and now stands in
// `data/presentation/gather.toml`. Every RATIO stayed in the recipe file as a
// named constant: which quadrant of the loop runs inbound, how far a rim sits
// outside its fill. That is the rule the combat recipes follow.

import { GATHER_LIMITS, GATHER_LIVE_LANE, MAP_PALETTE } from '../gen/presentation';
import type { GatherRow } from '../gen/presentation';
import type { DepositStyle } from '../ui/settings';
import {
  apexFor, arcFor, bezier2, cellKeys, clamp01, dashedRingFor, discFor, fillFor, liftInk, lineFor,
  polylineFor, rectFor, ringFor, softFor, strokeFor, TAU,
  type Pt, type SinkCtx,
} from './recipe-kit';
import { dashesOn } from './math';

export {
  PALE_INK, SOLID_KEY, TAU, WATER_INK, cellScale, clamp01, ease, frac, lerp, type Pt,
} from './recipe-kit';

export type { GatherRow };

// ---------------------------------------------------------------------------
// The inks
// ---------------------------------------------------------------------------
//
// The canvas is never themed (R1007), so a gather look wears the four inks of
// `ui-map-interface.md` §2 beside the commodity hue, the cargo brown and the
// faction colour its context already carries.

/** The white core of a flash and of a fresh cut: the bright ink of the
 *  live lane, white in the dark identity and the `core` role on paper
 *  (R3293). */
export const GATHER_WHITE = GATHER_LIVE_LANE.bright;
// `PALE_INK` (silt, a bubble, a jaw, a thruster puff, a work lamp) and
// `WATER_INK` (a hollow piece that must read as empty) are the shared kit's,
// passed on above.

/** The dim water: a slack line, a loop nobody is reading. It is the chrome
 *  `--dim` of the palette. */
export const DIM_INK = MAP_PALETTE.dim;

// ---------------------------------------------------------------------------
// The atlas cells
// ---------------------------------------------------------------------------

/** The four baked marks a recipe draws from, all baked WHITE. */
const GATHER_CELLS = ['glow', 'halo', 'wedge', 'chip'] as const;
export type GatherCell = (typeof GATHER_CELLS)[number];
const GATHER_CELL_KEYS = cellKeys('gather', GATHER_CELLS);

/**
 * The atlas key of one baked gather cell.
 *
 * The scheme lives here and `fast/gfx/atlas.ts gatherSpecFor` answers it: one
 * spelling, two readers. It is the rule `combatCellKey` follows.
 */
export function gatherCellKey(cell: GatherCell): string {
  return GATHER_CELL_KEYS[cell];
}

/**
 * The side of the `glow` and `halo` cells, in world px at scale 1.
 *
 * One size serves every card: a recipe scales the quad to the radius its row
 * states, so a figure change bakes nothing new.
 */
export const GATHER_CELL_PX = 64;

/**
 * How wide the `halo` ring stands, as a share of its own radius.
 *
 * The card writes `aRingGlow(c, x, y, r, w, …)` with a ring from `r − w` to
 * `r + w`. The ratio `w / r` is 0.35 at card 05's figures, and a baked ramp
 * carries ONE ratio, so the kit fixes it here and `ringGlow` takes the radius
 * alone. It is a PROPORTION and not a figure, which is why it is not in the
 * TOML.
 */
export const GATHER_HALO_WIDTH = 0.35;

/** The outer radius of a `halo` of radius `r`, which the quad is scaled to. */
export const GATHER_HALO_OUTER = 1 + GATHER_HALO_WIDTH;

/** The `wedge` cell's natural box: the apex at x = 0, the base at x = −1,
 *  the half-width 0.5. A recipe scales it to the length and the half-width
 *  its card draws, so `wedge` states nothing about the shape. */
export const GATHER_WEDGE_LEN = 1;

/** The `chip` cell's natural half axes: the satellite chip of the deposit
 *  mark, which is 3.2 px across and 4.2 px down at k = 1. */
export const GATHER_CHIP_HALF_X = 1.6;
export const GATHER_CHIP_HALF_Y = 2.1;

// ---------------------------------------------------------------------------
// The deposit geometry
// ---------------------------------------------------------------------------
//
// The field mark is the DRAIN mark of R475 (`render/marks.ts drawDeposit`). A
// look that draws ON the field reads these, so the line it puts on the level
// line stands where the fill ends.

/** The deposit diamond, clockwise from its top point, as a flat path. */
export const DEPOSIT_DIAMOND: readonly number[] = [0, -7, 5, 0, 0, 7, -5, 0];

/** The diamond's half height, which is the span the fill runs over. */
export const DEPOSIT_HALF_Y = 7;

/** The diamond's half width, at its own waist. */
const DEPOSIT_HALF_X = 5;

/**
 * How wide the diamond stands at the height `y`, in the field's own space.
 *
 * A card that CLIPS a line to the diamond reads it: the batches carry no
 * clip, so a line that the card cut at the rim is drawn to this span instead
 * (R1124). It answers 0 outside the diamond.
 */
export function diamondHalf(y: number): number {
  const d = DEPOSIT_HALF_Y - (y < 0 ? -y : y);
  return d <= 0 ? 0 : (DEPOSIT_HALF_X * d) / DEPOSIT_HALF_Y;
}

/** How many drain levels a field is quantised to (`render/marks.ts`). */
export const DEPOSIT_LEVELS = 8;

/**
 * The y of the level line of a field at `level`, in the field's own space.
 *
 * The fill is everything below it. A level of 0 puts the line at the bottom
 * apex, and a full field puts it at the top.
 */
export function levelY(level: number): number {
  const u = level <= 0 ? 0 : level >= DEPOSIT_LEVELS ? 1 : level / DEPOSIT_LEVELS;
  return DEPOSIT_HALF_Y - 2 * DEPOSIT_HALF_Y * u;
}

// ---------------------------------------------------------------------------
// The context
// ---------------------------------------------------------------------------

/**
 * ONE CUT, as a recipe reads it. The layer reuses ONE of these (R131).
 *
 * Every length is world px and every angle is radians, with +x east and +y
 * south. THE FIELD IS NOT THE ORIGIN here: a recipe adds `fx`/`fy` itself, or
 * takes a point out of `onField` / `inHull`. The fade `alpha`, the additive
 * flag, the three sinks and the atlas cell lookup are the `SinkCtx` of the
 * shared kit.
 */
export interface GatherCtx extends SinkCtx {
  /** The layer's clock in seconds of WALL time. A spin and a dash read it. */
  t: number;
  /** The camera scale: CSS px for one world px. */
  z: number;
  /** The figure row of the look that draws. */
  row: GatherRow;

  /** The deposit's centre, in world px. */
  fx: number;
  fy: number;
  /** The hull's centre, in world px. */
  hx: number;
  hy: number;
  /** The hull's heading now, and the heading it holds while it cuts. They
   *  differ only while the hull turns for home; a piece that stands on the
   *  FIELD reads `restHead`, and a piece that rides the HULL reads `head`. */
  head: number;
  restHead: number;

  /** The commodity hue of the field, and a lightened twin of it. */
  res: number;
  resHi: number;
  /** How many satellite chips the deposit mark carries: 1, 2 or 3. It says
   *  the commodity without colour, the way the mark does (§6.2). */
  chips: number;
  /** The cargo brown (R258). */
  cargo: number;
  /** The hull's faction ink. */
  ink: number;
  /**
   * The deposit mark the reader's Marks theme picked (R832, R1124).
   *
   * A look that draws ON the field reads it: four of the six values carry the
   * diamond and its level line, and `tag` and `tally` carry neither. The pick
   * is one value for the whole map, so the layer writes it once a frame.
   */
  deposit: DepositStyle;
  /**
   * True on the ONE cut that speaks for its field this frame (R1124).
   *
   * A look that draws on the FIELD rather than on the hull must test it,
   * because a stack of Dredgers on one deposit must draw ONE mark. The lowest
   * fleet id on the field leads, so the pick holds while the stack stands.
   */
  fieldLead: boolean;

  // -- the state of the cut (R1121) -----------------------------------------

  /** 0 to 1, the envelope of the cut: it ramps in over `rampInSec` after the
   *  hull settles and out over `rampOutSec` before the hold is full. Multiply
   *  by it. */
  work: number;
  /** 1 for a gather, `GATHER_LIMITS.shaftRate` for a shaft (R715). */
  rate: number;
  /** True in the shaft state. Every look says it apart by shape or rhythm. */
  shaft: boolean;
  /** Seconds of cut so far in this visit. */
  tw: number;
  /** 0 to 1, the phase inside the present lot. One lot takes
   *  `GATHER_LIMITS.lotSec / rate` of WALL time, so every look beats in one
   *  time however fast the map runs. */
  lot: number;
  /** How many lots this visit has lifted. */
  lotN: number;
  /**
   * 0 to 1, the hold, or −1 where the viewer cannot read it (R1121).
   *
   * A faction hull carries no hold on the wire, so a look that GAUGES the
   * hold must test this and draw its cut alone where it is −1.
   */
  fill: number;
  /** 1 falling to 0 over `fullSec` from the moment the hold filled. */
  full: number;
  /** 0 to `DEPOSIT_LEVELS`, the field's drain level. 0 is worked out. */
  level: number;
  /** 1 falling to 0 over `stepSec` after the field crossed a level line. */
  step: number;
  /** 1 falling to 0 over `bloomSec` after the field silted back up. */
  bloom: number;
  /** True while the hull sits on the field. */
  atRest: boolean;
  /** 1 while the hull is on the chart, falling to 0 as it leaves. */
  vis: number;
  /** 0 to 1 over the run home, else 0. */
  leaving: number;
  /** True when the hold carries anything. */
  laden: boolean;

  /** The unit vector of the current, for anything that drifts. */
  curX: number;
  curY: number;

  /** A stable seed for every grain of this cut. */
  seed: number;
}

/** One hook of a look: what it draws in one of the three passes. */
export type GatherHook = (c: GatherCtx) => void;

/**
 * ONE HARVESTING LOOK.
 *
 * The three hooks are the three hooks of the mockup page, and each one is its
 * own layer slot, because the engine flushes one layer whole before the next
 * (§6.2.1):
 *
 *   `under`  below the field mark      (slot `gatherunder`, before `sites`)
 *   `mid`    above the field mark and below the hull  (slot `gathermid`)
 *   `over`   above everything          (slot `gatherover`, after `fleets`)
 *
 * A hook a look does not need is left out, and the layer packs nothing for it.
 *
 * The three switches are what a look takes away from ANOTHER layer. They are
 * the `opts` of the mockup page, and they are exact rather than an overpaint
 * in the water ink.
 */
export interface GatherRecipe {
  under?: GatherHook;
  mid?: GatherHook;
  over?: GatherHook;
  /** The look draws the laden ring itself, so `FleetLayer` leaves out its own
   *  ring on that hull (`setLadenSuppressed`). Card 11's `opts.ring = false`. */
  ownsLadenRing?: boolean;
  /**
   * The look draws the deposit mark itself, so `SiteLayer` leaves out that
   * field's own sprite (`setFieldSuppressed`). It is card 16's
   * `opts.field = false`.
   *
   * NO SHIPPED LOOK SETS IT (R1124). `drain-pulse` draws OVER the picked
   * mark instead, because the mark is the reader's own Marks pick and two of
   * its six values carry neither a diamond nor a baked letter a recipe could
   * draw. The switch stays for a look that must replace the mark.
   */
  drawsField?: boolean;
}

// ---------------------------------------------------------------------------
// The arithmetic
// ---------------------------------------------------------------------------

/**
 * A fixed grain in 0 to 1 for the integer `k`, so a mote keeps its place.
 *
 * It is `hash` of the mockup page, byte for byte, so a card's scatter is the
 * card's own scatter. A recipe mixes in `c.seed` the way the cards do
 * (`k + sc.seed * 97`), and never calls `Math.random`.
 */
export function grain(k: number): number {
  const v = Math.sin(k * 127.1 + 311.7) * 43758.5453;
  return v - Math.floor(v);
}

/** How far apart two cuts' grains stand, the mockup page's `sc.seed * 97`. */
export const GRAIN_STRIDE = 97;

/**
 * A lighter twin of an ink: `k` of the way to white. It is `lighten` of the
 * page, which is the `lift` of `marks.ts` (R830) with the page's own default.
 * On paper it moves toward the core ink (`recipe-kit.ts` `LIFT_INK`, R3293).
 *
 * The layer builds `c.resHi` with it once a commodity, so a recipe that wants
 * the default lift reads the field rather than calling this a frame.
 */
export function lighten(hex: number, k = LIGHTEN_DEFAULT): number {
  return liftInk(hex, k);
}

/** How far toward white `lighten` goes when a caller names no share. */
const LIGHTEN_DEFAULT = 0.35;

// -- the envelopes ----------------------------------------------------------
//
// The eight envelopes the cards share. Each one is the card's own helper,
// renamed to say what it answers rather than which group of cards wrote it.

/**
 * A piece that must STOP before it fades: it follows the cut on the way in,
 * holds through the out-ramp, and then fades with the full-hold beat. It is
 * `aHold`.
 */
export function holdEnv(c: GatherCtx): number {
  if (c.full > 0) return c.full;
  if (!c.atRest) return 0;
  return c.tw > 1 ? 1 : c.work;
}

/**
 * The seconds a wheel turned so far: the integral of `work` over the cut, so
 * the wheel runs up over the in-ramp and runs down over the out-ramp. It is
 * `aTurn`.
 */
export function turnSec(c: GatherCtx): number {
  const tw = c.tw;
  const inn = GATHER_LIMITS.rampInSec, out = GATHER_LIMITS.rampOutSec;
  let s = tw < inn ? (tw * tw) / (2 * inn) : tw - inn / 2;
  if (tw > 1 && c.work < 1) {
    const d = out * (1 - c.work);
    s -= (d * d) / (2 * out);
  }
  return s;
}

/** The seconds since the cut ended, or 0 while it runs; big once the hull
 *  left. It is `aSince`. */
export function sinceCut(c: GatherCtx): number {
  if (c.full > 0) return (1 - c.full) * GATHER_LIMITS.fullSec;
  if (c.leaving > 0) return GATHER_LIMITS.fullSec + c.leaving * LEAVE_SEC;
  if (c.atRest) return 0;
  return c.tw > 0 ? LEFT_LONG_AGO : 0;
}

/** The seconds the run home takes, which `sinceCut` and `tauOf` age by. It is
 *  the card's `T_LEAVE`. */
const LEAVE_SEC = 2.7;

/** What `sinceCut` answers for a hull that is simply not there. */
const LEFT_LONG_AGO = 99;

/** The seconds since the cut began. It runs on through the full hold and the
 *  run home, where `tw` stands. It is `bTau`. */
export function tauOf(c: GatherCtx): number {
  return c.tw
    + (c.full > 0 ? (1 - c.full) * GATHER_LIMITS.fullSec : 0)
    + (c.leaving > 0 ? GATHER_LIMITS.fullSec + c.leaving * LEAVE_SEC : 0);
}

/** 1 while the hull sits on the field, a short fade as it turns home, else 0.
 *  It is `bStay`: `onEnv` on the field, and the fade off it. */
export function stayEnv(c: GatherCtx): number {
  if (c.atRest) return onEnv(c);
  return c.leaving > 0 ? clamp01(1 - c.leaving * LEAVE_FADE) : 0;
}

/** How fast `stayEnv` drops a piece once the hull turns home. */
const LEAVE_FADE = 8;

/** A piece rigged for the cut alone: it follows the cut, holds through the
 *  full-hold beat, and is gone on the run home. It is `cRig`. */
export function rigEnv(c: GatherCtx): number {
  return c.atRest ? Math.max(c.work, clamp01(c.full * FULL_HOLD_SNAP)) : 0;
}

/** How hard the full-hold beat snaps a rigged piece to full ink. */
const FULL_HOLD_SNAP = 4;

/** The presence of a working hull on the field: it ramps in with the cut and
 *  is 0 the moment the hull runs home. It is `dOn`. */
export function onEnv(c: GatherCtx): number {
  return c.atRest ? clamp01(c.tw / GATHER_LIMITS.rampInSec) : 0;
}

/** The beat of the lot: 1 at the start of a lot, falling to 0. It is `dBeat`. */
export function lotBeat(c: GatherCtx): number {
  const k = 1 - c.lot;
  return k * k;
}

// -- the geometry -----------------------------------------------------------

/** A point of HULL space as a world point: +x is the bow, +y is starboard.
 *  It is `sc.L`. */
export function inHull(c: GatherCtx, x: number, y: number, out: Pt): Pt {
  const cos = Math.cos(c.head), sin = Math.sin(c.head);
  out.x = c.hx + cos * x - sin * y;
  out.y = c.hy + sin * x + cos * y;
  return out;
}

/** A point of FIELD space as a world point: the field's own frame, unturned. */
export function onField(c: GatherCtx, x: number, y: number, out: Pt): Pt {
  out.x = c.fx + x;
  out.y = c.fy + y;
  return out;
}

/** A point of field space turned to `ang` about the field. It is the
 *  `c.rotate(bRestA())` of cards 07 and 08. */
export function spin(c: GatherCtx, x: number, y: number, ang: number, out: Pt): Pt {
  const cos = Math.cos(ang), sin = Math.sin(ang);
  out.x = c.fx + cos * x - sin * y;
  out.y = c.fy + sin * x + cos * y;
  return out;
}

/** A point on the quadratic curve A, C, B at `s`. It is `dQuad`. */
export function quadAt(
  ax: number, ay: number, cx: number, cy: number,
  bx: number, by: number, s: number, out: Pt,
): Pt {
  out.x = bezier2(ax, cx, bx, s);
  out.y = bezier2(ay, cy, by, s);
  return out;
}

// -- the clamps -------------------------------------------------------------

/** A fill alpha, held under the ceiling the limits state. */
export const fill = fillFor(GATHER_LIMITS);

/** A stroke alpha, held under the ceiling the limits state. */
export const stroke = strokeFor(GATHER_LIMITS);

// ---------------------------------------------------------------------------
// The primitives
// ---------------------------------------------------------------------------
//
// The shared kit's factories, bound once to `[gather.limits]`. Each one
// clamps its alpha, multiplies by `c.alpha` and skips a mark that would draw
// nothing.

/** A hard filled circle. It is `disc` of the page. */
export const disc = discFor<GatherCtx>(GATHER_LIMITS);

/** A stroked full circle. It is `circle` of the page with no dash. */
export const ring = ringFor<GatherCtx>(GATHER_LIMITS);

/** A stroked partial circle, from `start` over `sweep` radians. The page's
 *  `arc` takes a start and an END, so a recipe ported from a card passes
 *  `a1 - a0` here. */
export const arcOf = arcFor<GatherCtx>(GATHER_LIMITS);

/**
 * A dashed ring: `n` dashes, each covering `duty` of its own step.
 *
 * It is `circle(…, [on, off])` of the page. The page states a dash in px and
 * the sink states it as a count and a duty, so a recipe turns the card's
 * `[on, off]` into `n = round(TAU * r / (on + off))` and `duty = on / (on +
 * off)` — which is what the card's own dash does at that radius.
 */
export const dashedRing = dashedRingFor<GatherCtx>(GATHER_LIMITS);

/** How many dashes a ring of radius `r` takes at the card's `[on, off]`
 *  pattern (`render/math.ts`), and how much of a step the dash covers. */
export { dashesOn };
export function dashDuty(on: number, off: number): number {
  const step = on + off;
  return step > 0 ? clamp01(on / step) : 1;
}

/** A round-capped segment. It is `line` of the page. A dash pattern in
 *  world px is left out where it is not wanted; the segment sink has no dash
 *  PHASE, so a card that animates the phase walks the dashes with
 *  `polyline`. */
export const line = lineFor<GatherCtx>(GATHER_LIMITS);

/** A polyline of `n` points read out of the caller's own scratch `xs` and
 *  `ys`: `n − 1` segments, closed when asked. It is the shimmer line of card
 *  16, the spiral arms of card 20 and the hose of the page's curved pieces. */
export const polyline = polylineFor<GatherCtx>(line);

/**
 * A closed polygon read out of a FLAT path `[x0, y0, x1, y1, …]`, offset to
 * `(x, y)` and scaled by `k`: one segment per side.
 *
 * It is `PF(c, path)` followed by `S(…)` on the page. The sinks draw no
 * filled polygon, so a card that FILLS one takes `wedge` or `chip` instead.
 */
export function path(
  c: GatherCtx, p: readonly number[], x: number, y: number, k: number,
  w: number, ink: number, a: number,
): void {
  const n = p.length >> 1;
  if (n < 2) return;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    line(
      c, x + p[i * 2] * k, y + p[i * 2 + 1] * k,
      x + p[j * 2] * k, y + p[j * 2 + 1] * k, w, ink, a,
    );
  }
}

/** The deposit diamond as an outline, at `(x, y)` and scaled by `k`. It is
 *  `PF(c, DIAMOND); S(…)` of cards 08 and 16. */
export function diamond(
  c: GatherCtx, x: number, y: number, k: number, w: number, ink: number, a: number,
): void {
  path(c, DEPOSIT_DIAMOND, x, y, k, w, ink, a);
}

/** A rectangle outline of `w` by `h`, centred on the point and turned to
 *  `rot`: four segments. It is `strokeRect` on the page. */
export function box(
  c: GatherCtx, x: number, y: number, w: number, h: number, rot: number,
  stroke2: number, ink: number, a: number,
): void {
  if (w <= 0 || h <= 0 || stroke2 <= 0) return;
  const cos = Math.cos(rot), sin = Math.sin(rot);
  const hw = w / 2, hh = h / 2;
  for (let i = 0; i < 4; i++) {
    const sx = i === 0 || i === 3 ? -hw : hw;
    const sy = i < 2 ? -hh : hh;
    const j = (i + 1) % 4;
    const ex = j === 0 || j === 3 ? -hw : hw;
    const ey = j < 2 ? -hh : hh;
    line(
      c, x + cos * sx - sin * sy, y + sin * sx + cos * sy,
      x + cos * ex - sin * ey, y + sin * ex + cos * ey, stroke2, ink, a,
    );
  }
}

/** A soft filled patch: the two-stop ramp of the page's `glow`. */
export const glow = softFor<GatherCtx>(GATHER_LIMITS, gatherCellKey('glow'));

/**
 * A soft HOLLOW ring of light at radius `r`: the page's `aRingGlow`.
 *
 * The ring's width is `GATHER_HALO_WIDTH` of its radius, because the ramp is
 * baked at one ratio, and the quad is scaled to the ring's OUTER radius. A
 * card that wants a tighter ring draws a `ring` beside it rather than asking
 * for another bake.
 */
export const ringGlow = softFor<GatherCtx>(
  GATHER_LIMITS, gatherCellKey('halo'), GATHER_HALO_OUTER,
);

/** A flat rectangle of `w` by `h`, centred on the point and turned to `rot`.
 *  It is `fillRect` under a rotate. */
export const rect = rectFor<GatherCtx>(GATHER_LIMITS);

/** A filled wedge, its APEX at `(x, y)`, turned to `ang`, `len` long and
 *  `hw` half wide at its base. It is the jaw of card 08, the saw tooth of
 *  card 01 and the thruster puff of the page's station-keeping piece. */
export const wedge = apexFor<GatherCtx>(
  GATHER_LIMITS, gatherCellKey('wedge'), GATHER_WEDGE_LEN,
);

/** A filled satellite chip at scale `k`, screen-aligned. It is `bChip`
 *  followed by a fill. */
export function chip(
  c: GatherCtx, x: number, y: number, k: number, ink: number, a: number,
): void {
  if (k <= 0) return;
  const al = fill(a) * c.alpha;
  if (al <= 0) return;
  const e = c.sprite(gatherCellKey('chip'));
  c.quads.quad(
    x, y, e.ox * k, e.oy * k, e.w * k, e.h * k, 0, 0, ink, al,
    e.u0, e.v0, e.u1, e.v1,
  );
}

/** The same chip as an outline: four segments. It is `bChip` followed by a
 *  stroke, which is how every card draws a SHAFT chip. */
export function chipOutline(
  c: GatherCtx, x: number, y: number, k: number, w: number, ink: number, a: number,
): void {
  if (k <= 0) return;
  path(c, CHIP_PATH, x, y, k, w, ink, a);
}

/** The chip's own flat path at k = 1, the proportion of a satellite chip. */
const CHIP_PATH: readonly number[] = [
  0, -GATHER_CHIP_HALF_Y, GATHER_CHIP_HALF_X, 0,
  0, GATHER_CHIP_HALF_Y, -GATHER_CHIP_HALF_X, 0,
];

/**
 * An ellipse outline of half axes `rx` and `ry`, turned to `rot`, walked as
 * `n` segments.
 *
 * The circle sink draws a CIRCLE, so an ellipse is a polyline. It is the loop
 * of card 07. `xs`/`ys` are the caller's own scratch and must hold `n + 1`.
 */
export function ellipse(
  c: GatherCtx, x: number, y: number, rx: number, ry: number, rot: number,
  n: number, xs: Float32Array, ys: Float32Array,
  w: number, ink: number, a: number, dashOn = 0, dashOff = 0,
): void {
  if (rx <= 0 || ry <= 0 || n < 3) return;
  const cos = Math.cos(rot), sin = Math.sin(rot);
  for (let i = 0; i <= n; i++) {
    const th = (i / n) * TAU;
    const px = Math.cos(th) * rx, py = Math.sin(th) * ry;
    xs[i] = x + cos * px - sin * py;
    ys[i] = y + sin * px + cos * py;
  }
  // One segment a step, each with the dash the caller named: a dashed loop
  // restarts its dash at every step, because the sink has no dash phase.
  for (let i = 1; i <= n; i++) {
    line(c, xs[i - 1], ys[i - 1], xs[i], ys[i], w, ink, a, dashOn, dashOff);
  }
}
